/**
 * AP-37 Detailblatt O 6.4 — **Preisanfragen nacheinander senden.**
 *
 * Der Server des Belegwesens bleibt, wie er ist (O-14): je Anfrage im Entwurf
 * zuerst **Festschreiben**, sofern die Belegart es vor dem Senden verlangt, dann
 * der gemeinsame `SendDialog` — „Preisanfrage 2 von 3 — Stahl Maier" — mit
 * Überspringen und Abbrechen. Übersprungene bleiben im Entwurf. Den Status setzt
 * der Rückweg des Belegwesens; das Cockpit lädt danach nur neu.
 */
import { useEffect, useRef, useState } from 'react'
import { SendDialog } from '@/components/belege/SendDialog'
import { versand } from '@/components/belege/versand'
import { api } from '@/lib/axios'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'

/** Was das Cockpit vom Beleg braucht — ein Ausschnitt der Belegansicht. */
interface BelegKurz {
  id: string
  label: string
  status: string
  requirePosting: boolean
  rights: { post: boolean; send: boolean }
}

export interface ZuSenden {
  documentId: string
  supplierName: string
}

export function AnfragenSenden({ belege, onFertig }: { belege: ZuSenden[]; onFertig: (ergebnis: { gesendet: number; uebersprungen: string[]; fehler: string[] }) => void }) {
  const [index, setIndex] = useState(0)
  const [beleg, setBeleg] = useState<BelegKurz | null>(null)
  const [gesendet, setGesendet] = useState(0)
  const [uebersprungen, setUebersprungen] = useState<string[]>([])
  const [fehler, setFehler] = useState<string[]>([])
  const [bestaetigt, setBestaetigt] = useState<boolean | null>(null)
  const erfolg = useRef(false)

  const aktuell = belege[index]

  // Einmal fragen, bevor irgendetwas festgeschrieben wird — danach gilt die Antwort für alle.
  // Die Marke verhindert die zweite Frage, wenn React den Effekt doppelt ausführt (StrictMode).
  const gefragt = useRef(false)
  useEffect(() => {
    if (gefragt.current) return
    gefragt.current = true
    void bestaetige(
      belege.length === 1
        ? 'Die Preisanfrage wird vor dem Senden festgeschrieben, falls die Belegart es verlangt — danach lässt sie sich nicht mehr ändern.'
        : `${belege.length} Preisanfragen werden nacheinander gesendet und dafür festgeschrieben, falls die Belegart es verlangt — danach lassen sie sich nicht mehr ändern.`,
      { ok: 'Weiter' },
    ).then(setBestaetigt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (bestaetigt === false) onFertig({ gesendet: 0, uebersprungen: belege.map((b) => b.supplierName), fehler: [] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestaetigt])

  useEffect(() => {
    if (!bestaetigt) return
    if (index >= belege.length) {
      onFertig({ gesendet, uebersprungen, fehler })
      return
    }
    setBeleg(null)
    let ab = false
    const vorbereiten = async () => {
      try {
        let b = (await versand.lesen<BelegKurz>(aktuell.documentId)).data.data
        if (b.status === 'DRAFT' && b.requirePosting) {
          if (!b.rights.post) throw new Error('Zum Festschreiben fehlt das Recht.')
          b = (await api.post<{ data: BelegKurz }>(`/documents/${b.id}/post`)).data.data
        }
        if (!b.rights.send) throw new Error('Zum Senden fehlt das Recht.')
        if (!ab) setBeleg(b)
      } catch (e) {
        if (ab) return
        setFehler((f) => [...f, `${aktuell.supplierName}: ${apiErrorText(e, (e as Error).message || 'nicht vorbereitet')}`])
        setIndex((i) => i + 1)
      }
    }
    void vorbereiten()
    return () => {
      ab = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, bestaetigt])

  if (!bestaetigt || !beleg || !aktuell) return null
  return (
    <SendDialog<BelegKurz>
      open
      beleg={beleg}
      kopfzeile={belege.length > 1 ? `Preisanfrage ${index + 1} von ${belege.length} — ${aktuell.supplierName}` : `Preisanfrage — ${aktuell.supplierName}`}
      onSent={() => undefined}
      onGesendet={() => {
        erfolg.current = true
      }}
      onClose={() => {
        if (erfolg.current) {
          erfolg.current = false
          setGesendet((g) => g + 1)
          setIndex((i) => i + 1)
          return
        }
        // Abbrechen beendet die Reihe — der Rest bleibt im Entwurf.
        onFertig({ gesendet, uebersprungen: [...uebersprungen, ...belege.slice(index).map((b) => b.supplierName)], fehler })
      }}
      onSkip={
        belege.length > 1
          ? () => {
              setUebersprungen((u) => [...u, aktuell.supplierName])
              setIndex((i) => i + 1)
            }
          : undefined
      }
    />
  )
}
